"use client";

import { useEffect, useMemo } from "react";
import {
  X,
  Loader2,
  Armchair,
  CreditCard,
  ShoppingCart,
  Trash2,
  Ban,
  Info,
  Tag,
} from "lucide-react";

const statusConfig = {
  other: {
    text: "Ezt a hely foglalt.",
    icon: <Ban className="w-4 h-4" />,
    bgClass: "bg-red-500/10 dark:bg-red-500/15",
    textClass: "text-red-600 dark:text-red-400",
    borderClass: "border-red-500/20 dark:border-red-500/20",
  },
  "mine-ordered": {
    text: "Ezt a helyet már korábban megvásároltad.",
    icon: <Info className="w-4 h-4" />,
    bgClass: "bg-orange-500/10 dark:bg-orange-500/15",
    textClass: "text-orange-600 dark:text-orange-400",
    borderClass: "border-orange-500/20 dark:border-orange-500/20",
  },
  mine: {
    text: "Ez a hely jelenleg a kosaradban van.",
    icon: <ShoppingCart className="w-4 h-4" />,
    bgClass: "bg-amber-500/10 dark:bg-amber-500/15",
    textClass: "text-amber-600 dark:text-amber-400",
    borderClass: "border-amber-500/20 dark:border-amber-500/20",
  },
  redeemed: {
    text: "Belépett.",
    icon: <Ban className="w-4 h-4" />,
    bgClass: "bg-red-500/10 dark:bg-red-500/15",
    textClass: "text-red-600 dark:text-red-400",
    borderClass: "border-red-500/20 dark:border-red-500/20",
  },
  swapped_redeemed: {
    text: "Belépett.",
    icon: <Ban className="w-4 h-4" />,
    bgClass: "bg-red-500/10 dark:bg-red-500/15",
    textClass: "text-red-600 dark:text-red-400",
    borderClass: "border-red-500/20 dark:border-red-500/20",
  },
  in_cart: {
    text: "Ez a hely már más kosarában van.",
    icon: <ShoppingCart className="w-4 h-4" />,
    bgClass: "bg-red-500/10 dark:bg-red-500/15",
    textClass: "text-red-600 dark:text-red-400",
    borderClass: "border-red-500/20 dark:border-red-500/20",
  },
};

export default function SeatModal({
  open,
  pendingSeatId,
  seats,
  reservedSeats,
  seatmapVariants,
  seatCategoryPrices,
  t,

  renderVariantPrice,
  renderHandlingFee,

  onClose,
  onConfirmSeat,
  onSelectVariant,
  seatConfirmLoading,
}) {
  const seat = useMemo(() => {
    if (!pendingSeatId) return null;
    return seats?.find((s) => String(s.id) === String(pendingSeatId)) || null;
  }, [pendingSeatId, seats]);

  const reservedEntry = useMemo(() => {
    if (!seat) return null;
    return reservedSeats?.find((r) => String(r.seatId) === String(seat.id)) || null;
  }, [reservedSeats, seat]);

  const isMine = reservedEntry?.status === "mine";
  const blockedStatuses = [
    "other",
    "mine-ordered",
    "redeemed",
    "swapped_redeemed",
    "in_cart",
  ];
  const isBlocked = blockedStatuses.includes(reservedEntry?.status);

  const variantsForSeat = useMemo(() => {
    if (!seat) return [];
    return (seatmapVariants || []).filter((variant) => {
      const seatCategoryAttr = variant.product_variant_attributes?.find(
        (attr) => attr?.attribute?.key === "seat_category"
      );

      const seatCategoryValue =
        seatCategoryAttr?.attribute_value?.key ||
        seatCategoryAttr?.custom_value_text ||
        seatCategoryAttr?.custom_value_number;

      return seatCategoryValue === seat.category;
    });
  }, [seat, seatmapVariants]);

  const showVariantPicker = !isBlocked && !isMine && variantsForSeat.length > 1;

  // Esc + Enter
  useEffect(() => {
    if (!open || !pendingSeatId) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (seatConfirmLoading) return;
        onClose?.();
        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        if (seatConfirmLoading) return;
        if (variantsForSeat.length > 1) return;
        if (isBlocked) return;

        Promise.resolve(onConfirmSeat?.(pendingSeatId))
          .then(() => onClose?.())
          .catch(() => { });
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    open,
    pendingSeatId,
    variantsForSeat.length,
    isBlocked,
    seatConfirmLoading,
    onClose,
    onConfirmSeat,
  ]);

  useEffect(() => {
    if (!open) return;

    // Mentjük az eredeti stílusokat, hogy pontosan vissza tudjuk állítani
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;

    // Ha eltűnik a scrollbar, ne ugorjon el a layout (desktopon)
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
    };
  }, [open]);

  if (!open || !pendingSeatId || !seat) return null;

  const priceInfo = seatCategoryPrices?.[seat.category] || null;
  const statusInfo = reservedEntry?.status ? statusConfig[reservedEntry.status] : null;

  const singleVariant = variantsForSeat?.[0] || null;
  const showSinglePriceRow = !!priceInfo && !showVariantPicker && !!singleVariant;

  const safeClose = () => {
    if (seatConfirmLoading) return;
    onClose?.();
  };

  const modalKey = `${pendingSeatId}-${showVariantPicker ? "picker" : "single"}`;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center z-[9999] pl-1 pr-1 pt-1 pb-11 md:p-4"
      onClick={safeClose}
    >
      <div
        key={modalKey}
        className="w-full md:max-w-md animate-in slide-in-from-bottom-4 md:slide-in-from-bottom-2 fade-in duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Panel (BoxBlock stílus szerint) */}
        <div className="rounded-2xl md:rounded-2xl bg-white dark:bg-zinc-900 shadow-2xl border border-gray-200 dark:border-zinc-700 overflow-hidden">
          {/* Header (BoxBlock header vibe) */}
          <div className="bg-gray-50 dark:bg-zinc-800 px-5 py-4 border-b border-gray-200 dark:border-zinc-700">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 tracking-tight">
                {t?.("seat.modal") || "Hely részletei"}
              </h2>

              <button
                onClick={safeClose}
                className="p-0 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-700 transition-colors cursor-pointer"
                aria-label="Close"
                type="button"
              >
                <X className="w-7 h-7 text-gray-500 dark:text-gray-300" />
              </button>
            </div>
          </div>

          {/* Seat Info */}
          <div className="px-5 py-4 space-y-3">
            {/* Hero */}
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-100 dark:bg-zinc-800">
                <Armchair className="w-5 h-5 text-gray-900 dark:text-gray-100" />
              </div>

              <div>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100 leading-tight">
                  {seat.name}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t?.("seat.modal.category") || "Kategória"}{" "}
                  {t?.(seat.category) || seat.category}
                </p>
              </div>
            </div>

            {/* Price (single variant) */}
            {showSinglePriceRow && (
              <div className="flex items-center justify-between rounded-xl bg-gray-50 dark:bg-zinc-800/50 px-4 py-3">
                <div className="flex items-center gap-2.5">
                  <CreditCard className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {t("seat.modal.price")}
                  </span>
                </div>

                <div className="text-right">
                  <span className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    {renderVariantPrice?.(singleVariant, "custom")}
                  </span>

                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {renderHandlingFee?.(singleVariant)}
                  </div>
                </div>
              </div>
            )}

            {/* Status (helyfoglalással, hogy ne ugráljon) */}
            <div className="min-h-[52px]">
              {statusInfo ? (
                <div
                  className={`flex items-center gap-2.5 rounded-xl px-4 py-3 border ${statusInfo.bgClass} ${statusInfo.borderClass}`}
                >
                  <span className={statusInfo.textClass}>{statusInfo.icon}</span>
                  <p className={`text-sm font-medium ${statusInfo.textClass}`}>
                    {statusInfo.text}
                  </p>
                </div>
              ) : (
                // üres placeholder ugyanakkora belső paddal, csak láthatatlan
                <div className="flex items-center gap-2.5 rounded-xl px-4 py-3 border border-transparent opacity-0 select-none">
                  <span className="w-4 h-4" />
                  <p className="text-sm font-medium">placeholder</p>
                </div>
              )}
            </div>
          </div>

          {/* Variant picker */}
          {showVariantPicker && (
            <div className="px-5 pb-2">
              <div className="flex items-center gap-2 mb-3">
                <Tag className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                <span className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {"Válassz jegytípust"}
                </span>
              </div>

              <div className="space-y-2">
                {variantsForSeat.map((variant) => (
                  <button
                    key={variant.id}
                    onClick={async () => {
                      if (seatConfirmLoading) return;
                      try {
                        await onSelectVariant?.(pendingSeatId, variant.id);
                        onClose?.();
                      } catch (e) { }
                    }}
                    className="w-full flex items-center justify-between rounded-xl border border-gray-200 dark:border-zinc-700 bg-gray-50/60 dark:bg-zinc-800/30 hover:bg-gray-100 dark:hover:bg-zinc-800 hover:border-gray-300 dark:hover:border-zinc-600 px-4 py-3.5 transition-all group cursor-pointer"
                    type="button"
                  >
                    <div className="flex flex-col text-left">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {variant?.name?.trim() || variant?.sku || ""}
                      </span>

                      <span className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {renderVariantPrice?.(variant, "custom")}
                      </span>

                      {variant.description && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {variant.description}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-black text-white dark:bg-white dark:text-black opacity-0 group-hover:opacity-100 transition-opacity">
                        <ShoppingCart className="w-3.5 h-3.5" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Footer CTA */}
          {!showVariantPicker && (
            <div className="bg-gray-50 dark:bg-zinc-800 px-5 py-4 border-t border-gray-200 dark:border-zinc-700">
              {(reservedEntry?.orderId || reservedEntry?.cartId) && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3">
                  {reservedEntry?.orderId && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {"Rendelési szám: "}
                      <span className="font-mono text-gray-900 dark:text-gray-100">
                        {reservedEntry.orderId}
                      </span>
                    </p>
                  )}

                  {reservedEntry?.cartId && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {"Kosár ID: "}
                      <span className="font-mono text-gray-900 dark:text-gray-100">
                        {reservedEntry.cartId}
                      </span>
                    </p>
                  )}
                </div>
              )}

              <button
                disabled={isBlocked || seatConfirmLoading}
                onClick={async () => {
                  if (isBlocked) return;
                  await onConfirmSeat?.(pendingSeatId);
                  onClose?.();
                }}
                className={`
                  w-full flex items-center justify-center gap-2
                  px-4 py-3 rounded-xl text-sm font-semibold
                  transition-all duration-200
                  ${isBlocked || seatConfirmLoading
                    ? "cursor-not-allowed bg-gray-200 text-gray-500 dark:bg-zinc-700 dark:text-gray-300"
                    : isMine
                      ? "cursor-pointer bg-red-600 hover:bg-red-700 text-white"
                      : "cursor-pointer bg-black hover:bg-gray-800 text-white dark:bg-white dark:text-black dark:hover:bg-zinc-200"
                  }
                `}
                type="button"
              >

                {seatConfirmLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t?.("product.add.to.cart.loading")}
                  </>
                ) : (
                  <>
                    {isMine && <Trash2 className="w-4 h-4" />}

                    {!isMine && !isBlocked && <ShoppingCart className="w-4 h-4" />}

                    {t?.(
                      isBlocked
                        ? "product.not_available"
                        : isMine
                          ? "product.remove_from_cart"
                          : "product.add.to.cart"
                    )}
                  </>
                )}

              </button>
            </div>
          )}

          {/* Mobile safe area */}
          <div className="h-safe-area-inset-bottom md:hidden" />
        </div>
      </div>
    </div>
  );
}